import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SectionLink from "./SectionLink";
import { AppearanceToggle } from "./PortfolioAppearance";
import { trackAction } from "./PageAnalytics";
import { CV_DOWNLOAD_URL, CV_DOWNLOAD_FILENAME } from "../../data/portfolioWork";
import imgAvatar from "../../assets/images/homepage/avatar.webp";

// Site header. On the homepage: the name, then Work and About (the page's own
// sections), CV for the PDF and the light / dark appearance. The footer
// repeats the same wording.
//
// Every other page passes `back`, the one way out it needs (see
// hooks/useBackLink): the bar becomes that link on the left, the name in the
// centre (still going home) and the appearance on the right, so no link points
// at sections of another page. With `steps` the link returns through history.
//
// Every control fits on one row down to 320px, so there is no menu. Below 420
// CV drops out of the row (the footer still carries it), the page bar keeps
// only the avatar of the name, and below 360 the links trade some horizontal
// padding; the touch area stays 44 tall. The avatar is decorative: the link
// is named by the text beside it.
//
// At the top of the page the bar spans the content edge; once the page has
// scrolled it narrows into a centred, floating glass capsule (home.css).
const FLOAT_AFTER = 24;

function focusMain(event) {
  const main = document.getElementById("main");
  if (!main) return;
  event.preventDefault();
  main.focus();
}

function Name() {
  return (
    <Link to="/" className="pf-site-header__name">
      <img
        className="pf-site-header__avatar"
        src={imgAvatar}
        width="24"
        height="24"
        alt=""
        decoding="async"
      />
      <span className="pf-site-header__name-text">Oscar Rabadán</span>
    </Link>
  );
}

function Header({ back }) {
  const [floating, setFloating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const update = () => setFloating(window.scrollY > FLOAT_AFTER);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  return (
    <div className={`pf-site-header${floating ? " is-floating" : ""}`}>
      <a href="#main" className="pf-skip-link" onClick={focusMain}>
        Skip to content
      </a>
      {back ? (
        <header className="pf-site-header__bar pf-site-header__bar--page pf-shell">
          <Link
            to={back.to}
            className="pf-nav-link pf-site-header__back"
            onClick={(event) => {
              trackAction(back.to === "/" ? "open_home" : "open_work", "header");
              // A plain click returns through history; modified clicks keep
              // the link's own behaviour (new tab, new window).
              if (!back.steps || event.button !== 0) return;
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              navigate(-back.steps);
            }}
          >
            <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
            {back.label}
          </Link>
          <Name />
          <div className="pf-site-header__end">
            <AppearanceToggle />
          </div>
        </header>
      ) : (
        <header className="pf-site-header__bar pf-shell">
          <Name />
          <div className="pf-site-header__end">
            <nav aria-label="Main" className="pf-site-header__nav">
              <SectionLink section="selected-work" className="pf-nav-link">
                Work
              </SectionLink>
              <SectionLink section="about" className="pf-nav-link">
                About
              </SectionLink>
              <a
                href={CV_DOWNLOAD_URL}
                download={CV_DOWNLOAD_FILENAME}
                className="pf-nav-link pf-nav-link--cv"
                onClick={() => trackAction("download_cv", "header")}
              >
                <span className="pf-visually-hidden">Download </span>CV
                <span className="pf-visually-hidden"> (PDF)</span>
              </a>
            </nav>
            <AppearanceToggle />
          </div>
        </header>
      )}
    </div>
  );
}

export default Header;
